import type { JSX } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";

import type { IHookConfig } from "app/components/widget/types";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { FlexRow, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import {
    type WWOTopAdNetworksSIParams,
    WWOTopAdNetworksPieChartSI,
    WWOTopAdNetworksTableSI,
} from "./WWOTopAdNetworksSIComponents";

const HeaderStyle = styled(FlexRow)`
    justify-content: space-between;
    box-sizing: border-box;
    padding: 24px;
`;

const TopAdNetworksWrapper = styled.div<{ isCompare?: boolean }>`
    background-color: ${colorsPalettes.carbon[0]};
    height: 384px;
    box-shadow: 0 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    border-radius: 4px;
    ${({ isCompare = false }) => isCompare && `margin-bottom:24px;`};
`;

const CtaWrapper = styled(RightFlexRow)`
    height: 52px;
    border-top: 1px solid ${colorsPalettes.carbon[100]};

    button {
        margin: 5px 8px;
    }
`;

interface IWWOTopAdNetworksSIProps {
    getCtaLink: () => { href: string; target: string };
    isCompare: boolean;
    unlockHook?: IHookConfig;
    hideFooter?: boolean;
}

export const WWOTopAdNetworksSI = ({
    getCtaLink,
    isCompare,
    unlockHook,
    hideFooter = false,
}: IWWOTopAdNetworksSIProps) => {
    const i18n = i18nFilter();
    const { params } = useCurrentStateAndParams();
    const { country, key } = params;
    let { duration } = params;
    const { isWWWithoutPermissions } = websiteWorldWideOverviewLogic(country);

    if (isWWWithoutPermissions) {
        duration = BasicDurations.LAST_THREE_MONTHS;
    }

    const durationObject = DurationService.getDurationData(duration);
    const getDateRange = (durationData) => [durationData.raw.from, durationData.raw.to];
    const [fromDate, toDate] = getDateRange(durationObject);
    const title = i18n("wa.wwo.displayadnetwork.title");
    const tooltip = i18n("wa.wwo.displayadnetwork.title.tooltip");
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
            },
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country)?.text,
        },
        {
            filter: "webSource",
            value: "desktop",
        },
    ];
    const componentParams: WWOTopAdNetworksSIParams = {
        ...params,
        country,
        key,
        duration,
    };

    const getCta = (ctaClicked: () => void): JSX.Element | null => {
        if (hideFooter) {
            return null;
        }

        const link = getCtaLink();

        return (
            <CtaWrapper>
                <FooterComponent
                    href={link.href}
                    onClick={ctaClicked}
                    text="wwo.display.ad.network.button"
                    unlockHook={unlockHook}
                    target={link.target}
                />
            </CtaWrapper>
        );
    };

    return (
        <TopAdNetworksWrapper
            isCompare={isCompare}
            data-automation="wwo-top-ad-networks"
        >
            <HeaderStyle>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subTitleFilters}
                />
            </HeaderStyle>
            {isCompare ? (
                <WWOTopAdNetworksTableSI
                    getCta={getCta}
                    params={componentParams}
                    isCompare={isCompare}
                />
            ) : (
                <WWOTopAdNetworksPieChartSI
                    getCta={getCta}
                    params={componentParams}
                    isCompare={isCompare}
                />
            )}
        </TopAdNetworksWrapper>
    );
};
